import { create } from 'zustand';
import { RegistrationRequest, AuditLog, User, RequestStatus, MissionState } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface AppState {
  user: User | null;
  requests: RegistrationRequest[];
  auditLogs: AuditLog[];
  isDarkMode: boolean;
  mission: MissionState;

  // NEW: OTP session tracking
  otpSessionId: string | null;

  // Actions
  setUser: (user: User | null) => void;
  toggleDarkMode: () => void;
  addRequest: (req: RegistrationRequest) => void;
  updateRequestStatus: (id: string, status: RequestStatus) => void;
  addAuditLog: (log: Omit<AuditLog, 'id' | 'timestamp'>) => void;

  // Gamification
  completeMission: (id: string) => void;
  updateReadiness: (score: number) => void;

  // API
  sendOtp: (email: string) => Promise<boolean>;
  verifyOtp: (code: string) => Promise<User | null>;
}

export const useStore = create<AppState>((set, get) => ({

  user: null,
  otpSessionId: null, // ✅ NEW

  isDarkMode: true,

  mission: {
    completedMissions: [],
    readinessScore: 0,
    unlockedBadges: []
  },

  requests: [],
  auditLogs: [],

  setUser: (user) => set({ user }),

  toggleDarkMode: () =>
    set((state) => ({ isDarkMode: !state.isDarkMode })),

  addRequest: (req) =>
    set((state) => ({
      requests: [req, ...state.requests],
      auditLogs: [{
        id: `LOG-${Math.random().toString(36).substr(2, 5).toUpperCase()}`,
        action: 'SIGNUP_REQUEST',
        target: req.companyName,
        actor: 'External Requester',
        timestamp: new Date().toISOString()
      }, ...state.auditLogs]
    })),

  updateRequestStatus: (id, status) =>
    set((state) => ({
      requests: state.requests.map(r =>
        r.id === id ? { ...r, status } : r
      )
    })),

  addAuditLog: (log) =>
    set((state) => ({
      auditLogs: [{
        ...log,
        id: `LOG-${Math.random().toString(36).substr(2, 5).toUpperCase()}`,
        timestamp: new Date().toISOString()
      }, ...state.auditLogs]
    })),

  completeMission: (id) =>
    set((state) => {
      if (state.mission.completedMissions.includes(id)) return state;

      const newMissions = [...state.mission.completedMissions, id];
      const badges = [...state.mission.unlockedBadges];

      if (newMissions.length === 1) badges.push('Mission: First Contact');
      if (newMissions.length === 3) badges.push('Mission: Master Agent');

      return {
        mission: {
          ...state.mission,
          completedMissions: newMissions,
          unlockedBadges: badges
        }
      };
    }),

  updateReadiness: (score) =>
    set((state) => ({
      mission: { ...state.mission, readinessScore: score }
    })),

  // ============================
  // SEND OTP
  // ============================
  sendOtp: async (email: string): Promise<boolean> => {

    try {

      const res = await fetch(`${API_BASE}/api/auth/otp/send/`, {

        method: 'POST',

        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ email })
      });

      const data = await res.json();

      if (res.ok && data.success && data.session_id) {

        set({
          otpSessionId: data.session_id
        });

        return true;
      }

      // Surface server error message (e.g. pending signup)
      if (data.detail) {
        throw new Error(data.detail);
      }

      return false;

    } catch (err: any) {

      console.error('[sendOtp error]', err);
      // Re-throw with message so caller can display it
      if (err.message && err.message !== 'Failed to fetch') {
        throw err;
      }

      return false;
    }
  },

  // ============================
  // VERIFY OTP
  // ============================

  verifyOtp: async (code: string): Promise<User | null> => {

    try {

      const session_id = get().otpSessionId;

      if (!session_id) {

        console.error('[verifyOtp] Missing session_id');
        return null;
      }

      const res = await fetch(`${API_BASE}/api/auth/otp/verify/`, {

        method: 'POST',

        headers: {
          'Content-Type': 'application/json'
        },

        body: JSON.stringify({
          session_id,
          code
        })
      });

      const data = await res.json();

      // =========================
      // LOGIN SUCCESS
      // =========================

      if (res.ok && data.success && data.user && data.token) {

        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authUser', JSON.stringify(data.user));

        const user: User = {

          id: String(data.user.id),

          email: data.user.email,

          name: data.user.username,

          role: data.user.is_superuser ? 'admin' : 'user',

          permissions: data.user.is_superuser
            ? ['view_dashboard', 'manage_requests']
            : ['upload_documents']
        };

        set({
          user,
          otpSessionId: null // consume session
        });

        return user;
      }

      // =========================
      // PENDING APPROVAL
      // =========================

      if (res.status === 403 && data.status === "pending") {

        console.warn('[verifyOtp] Account pending approval');

        return {
          id: 'pending',
          email: '',
          name: 'Pending Approval',
          role: 'user',
          permissions: []
        };
      }

      // =========================
      // NOT REGISTERED (signup flow)
      // =========================

      if (res.status === 403 && data.status === "not_registered") {

        return {
          id: 'signup',
          email: '',
          name: 'Signup Verification',
          role: 'user',
          permissions: []
        };
      }

      // =========================
      // INVALID OTP
      // =========================

      console.warn('[verifyOtp] Invalid OTP or server rejection');

      return null;

    } catch (err) {

      console.error('[verifyOtp error]', err);

      return null;
    }
  },
}));